"use client"
import { type ReactNode, createContext, useRef, useContext } from "react"
import { useStore } from "zustand"

import { CourseStore, createCourseStore, initCourseState, CourseState } from "@/store/courseStore"

export type CourseStoreApi = ReturnType<typeof createCourseStore>

export const CourseStoreContext = createContext<CourseStoreApi | undefined>(undefined)

export interface CourseStoreProviderProps {
  children: ReactNode
  initialState: CourseState
}

export const CourseStoreProvider = ({
  children,
  initialState: providedInitialState,
}: CourseStoreProviderProps) => {
  const storeRef = useRef<CourseStoreApi | null>(null)
  if (storeRef.current === null) {
    storeRef.current = createCourseStore({ ...initCourseState(), ...providedInitialState })
  }

  return (
    <CourseStoreContext.Provider value={storeRef.current}>{children}</CourseStoreContext.Provider>
  )
}

export const useCourseStore = <T,>(selector: (store: CourseStore) => T): T => {
  const courseStoreContext = useContext(CourseStoreContext)

  if (!courseStoreContext) {
    throw new Error(`useCourseStore must be used within CourseStoreProvider`)
  }

  return useStore(courseStoreContext, selector)
}
